import produce from "immer";
import { createAsyncAction } from "typesafe-actions";
import { ActionType, createReducer } from "typesafe-actions";
import { Unit } from "../../../Types/UnitFeatureTypes";


export interface FetchUnitInfoState {
  request: {
      loading: boolean;
      error: string | null;
      userUnit: Unit | null;
      unitFile: any;
      data: Unit[] | null;
      count: string | null;
  };
}

export enum Types {
  FETCH_UNIT_REQUEST = "FETCH_UNIT_REQUEST",
  FETCH_UNIT_SUCCESS = "FETCH_UNIT_SUCCESS",
  FETCH_UNIT_FAILURE = "FETCH_UNIT_FAILURE",
  FETCH_UNIT_OCCUPANTS_REQUEST = "FETCH_UNIT_OCCUPANTS_REQUEST",
  FETCH_UNIT_OCCUPANTS_SUCCESS = "FETCH_UNIT_OCCUPANTS_SUCCESS",
  FETCH_UNIT_OCCUPANTS_FAILURE = "FETCH_UNIT_OCCUPANTS_FAILURE",
  FETCH_UNIT_BY_USER_REQUEST = "FETCH_UNIT_BY_USER_REQUEST",
  FETCH_UNIT_BY_USER_SUCCESS = "FETCH_UNIT_BY_USER_SUCCESS",
  FETCH_UNIT_BY_USER_FAILURE = "FETCH_UNIT_BY_USER_FAILURE",
  FETCH_UNIT_FILE_REQUEST = "FETCH_UNIT_FILE_REQUEST",
  FETCH_UNIT_FILE_SUCCESS = "FETCH_UNIT_FILE_SUCCESS",
  FETCH_UNIT_FILE_FAILURE = "FETCH_UNIT_FILE_FAILURE",
}

const unitActions = createAsyncAction(
  Types.FETCH_UNIT_REQUEST,
  Types.FETCH_UNIT_SUCCESS,
  Types.FETCH_UNIT_FAILURE,
)
<
  { id?: string, fresh?: boolean },
  { units: Unit[], count: string },
  { message: string }
>();

const unitByUserActions = createAsyncAction(
  Types.FETCH_UNIT_BY_USER_REQUEST,
  Types.FETCH_UNIT_BY_USER_SUCCESS,
  Types.FETCH_UNIT_BY_USER_FAILURE,
)
<
  { user_id: number },
  { unit: Unit },
  { message: string }
>();

const unitOccupantsActions = createAsyncAction(
  Types.FETCH_UNIT_OCCUPANTS_REQUEST,
  Types.FETCH_UNIT_OCCUPANTS_SUCCESS,
  Types.FETCH_UNIT_OCCUPANTS_FAILURE,
)
<
  { id: number },
  { occupants: {id: number, owner: {id: number, name: string}, tenants: {id: number, name: string}[] } },
  { message: string }
>();

const unitFileActions = createAsyncAction(
  Types.FETCH_UNIT_FILE_REQUEST,
  Types.FETCH_UNIT_FILE_SUCCESS,
  Types.FETCH_UNIT_FILE_FAILURE
)
<
  { id: number, fromDate: Date, toDate: Date },
  { unitFile: any },
  { message: string }
>();

export const Actions = { unitActions, unitOccupantsActions, unitByUserActions, unitFileActions };

export const initialState: FetchUnitInfoState = {
  request: {
    loading: false,
    error: null as string | null,
    unitFile: null,
    userUnit: null,
    data: null,
    count: '0'
  }
};

export type FetchUnitState = typeof initialState;

const fetchUnitRequest = (state = initialState) =>
  produce(state, (draft) => {
    draft.request.loading = true;
    draft.request.error = null;
    draft.request.data = null;

    return draft;
  });

const fetchUnitSuccess = (
  state = initialState,
  { payload }: ActionType<typeof Actions.unitActions.success>
) =>
  produce(state, (draft) => {
    draft.request.error = null;
    draft.request.data = payload.units;
    draft.request.count = payload.count;
    if (draft.request.data) {
      draft.request.loading = false;
    }

    return draft;
  });

const fetchUnitFailure = (
  state = initialState,
  { payload }: ActionType<typeof Actions.unitActions.failure>
) =>
  produce(state, (draft) => {
    draft.request.loading = false;
    draft.request.error = payload.message;

    return draft;
  });

const fetchUnitOccupantsRequest = (state = initialState) =>
  produce(state, (draft) => {
    draft.request.loading = true;
    draft.request.error = null;

    return draft;
  });

const fetchUnitOccupantsSuccess = (
  state = initialState,
  { payload }: ActionType<typeof Actions.unitOccupantsActions.success>
) =>
  produce(state, (draft) => {
    draft.request.error = null;
    const unit = draft.request.data?.find((unit) => unit.id === payload.occupants.id);
    if (unit) {
      unit.owner = payload.occupants.owner;
      unit.tenants = payload.occupants.tenants;
      draft.request.loading = false;
    }
  });

const fetchUnitOccupantsFailure = (
  state = initialState,
  { payload }: ActionType<typeof Actions.unitOccupantsActions.failure>
) =>
  produce(state, (draft) => {
    draft.request.loading = false;
    draft.request.error = payload.message;

    return draft;
  });

const fetchUnitByUserRequest = (state = initialState) =>
  produce(state, (draft) => {
    draft.request.loading = true;
    draft.request.error = null;

    return draft;
  });

const fetchUnitByUserSuccess = (
  state = initialState,
  { payload }: ActionType<typeof Actions.unitByUserActions.success>
) =>
  produce(state, (draft) => {
    draft.request.error = null;
    draft.request.userUnit = payload.unit;
    draft.request.loading = false;

    return draft;
  });

const fetchUnitByUserFailure = (
  state = initialState,
  { payload }: ActionType<typeof Actions.unitByUserActions.failure>
) =>
  produce(state, (draft) => {
    draft.request.loading = false;
    draft.request.error = payload.message;

    return draft;
  });

const fetchUnitFileRequest = (state = initialState) =>
  produce(state, (draft) => {
    draft.request.loading = true;
    draft.request.error = null;

    return draft;
  });

const fetchUnitFileSuccess = (
  state = initialState,
  { payload }: ActionType<typeof Actions.unitFileActions.success>
) =>
  produce(state, (draft) => {
    draft.request.error = null;
    draft.request.unitFile = payload.unitFile;
    draft.request.loading = false;

    return draft;
  });

const fetchUnitFileFailure = (
  state = initialState,
  { payload }: ActionType<typeof Actions.unitFileActions.failure>
) =>
  produce(state, (draft) => {
    draft.request.loading = false;
    draft.request.error = payload.message;

    return draft;
  });

export default createReducer<typeof initialState, ActionType<typeof Actions>>(
  initialState
)
  .handleAction(Actions.unitActions.request, fetchUnitRequest)
  .handleAction(Actions.unitActions.success, fetchUnitSuccess)
  .handleAction(Actions.unitActions.failure, fetchUnitFailure)
  .handleAction(Actions.unitOccupantsActions.request, fetchUnitOccupantsRequest)
  .handleAction(Actions.unitOccupantsActions.success, fetchUnitOccupantsSuccess)
  .handleAction(Actions.unitOccupantsActions.failure, fetchUnitOccupantsFailure)
  .handleAction(Actions.unitByUserActions.request, fetchUnitByUserRequest)
  .handleAction(Actions.unitByUserActions.success, fetchUnitByUserSuccess)
  .handleAction(Actions.unitByUserActions.failure, fetchUnitByUserFailure)
  .handleAction(Actions.unitFileActions.request, fetchUnitFileRequest)
  .handleAction(Actions.unitFileActions.success, fetchUnitFileSuccess)
  .handleAction(Actions.unitFileActions.failure, fetchUnitFileFailure);
  